import { manageFlowBookingSelector } from '@/features/manage/manageSelectors';
import { TransText } from '@/i18n/trans/text';
import { useSelector } from '@/store/utils';
import type { BookedTripDTO } from '@/types/dto/agent';
import {
  buildPassengerTypeByPassengerId,
  collectTripPassengerIds,
  getTripPassengers,
} from '@/utils/changeTrips';
import { cn } from '@/utils/cn';
import {
  Button,
  Icons,
  Popover,
  Typography,
} from '@packages/webshop-design-system';
import { type FC, useState } from 'react';

interface TripPassengerListPopoverProps {
  trip: BookedTripDTO;
  isDimmed?: boolean;
}

const TripPassengerListPopover: FC<TripPassengerListPopoverProps> = ({
  trip,
  isDimmed = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const booking = useSelector(manageFlowBookingSelector);
  const passengerCount = collectTripPassengerIds(trip).length;
  const passengers = getTripPassengers(trip, booking?.passengers ?? []);
  const passengerTypeByPassengerId = buildPassengerTypeByPassengerId(trip);

  return (
    <Popover
      isOpen={isOpen}
      onOpenChange={setIsOpen}
      placement="left top"
      className={{ root: 'min-w-[150px]' }}
      data-testid="trip-passenger-popover"
      trigger={
        <Button
          variant="ghost"
          endAdornment={<Icons.chevronDown width={16} height={16} />}
          className={cn('h-auto', isDimmed && 'text-neutral')}
          data-testid="trip-passenger-list-trigger"
        >
          <TransText
            i18nKey="passengersCount"
            values={{ count: passengerCount }}
          />
        </Button>
      }
      content={
        <div className="flex flex-1 flex-col gap-3">
          {passengers.map((passenger) => {
            const passengerType = passenger.id
              ? passengerTypeByPassengerId[passenger.id]
              : undefined;

            return (
              <div key={passenger.id} className="flex flex-col">
                <div className="flex flex-row gap-1">
                  <Typography variant="body2-bold">
                    {passenger.firstName}
                  </Typography>
                  <Typography variant="body2-bold">
                    {passenger.lastName}
                  </Typography>
                </div>
                {passengerType && (
                  <Typography variant="body2" className="text-neutral">
                    {passengerType}
                  </Typography>
                )}
              </div>
            );
          })}
        </div>
      }
    />
  );
};

export default TripPassengerListPopover;
